import React, { type ReactNode, useMemo } from 'react';
import { cn } from '@hero/web-lib';
import { MoreVerticalIcon } from '@hero/web-icons';
import type { ActionButtonGroup } from '../ActionButton/ActionButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/DropdownMenu/DropdownMenu';
import { Spinner } from '../Spinner';

export interface CardProps {
  children: ReactNode;
  onClick?: () => void;
  /** Action menu items. If provided, renders a three-dot menu in the top-right corner. */
  actions?: ActionButtonGroup[];
  className?: string;
}

/**
 * Reusable card wrapper with consistent background, border, shadow, and hover effects.
 * Use as the base for domain cards (OrderCard, DriverCard, etc.).
 *
 * When `onClick` is provided, the card becomes clickable but ignores clicks
 * when the user is selecting/copying text.
 */
export const Card: React.FC<CardProps> = ({ children, onClick, actions, className }) => {
  const handleClick = () => {
    if (!onClick) return;
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    onClick();
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'group relative flex flex-col gap-3 rounded-xl border bg-white p-4 shadow-sm transition-all duration-200',
        onClick
          ? 'cursor-pointer border-gray-150 hover:border-primary/30 hover:shadow-md hover:-translate-y-0.5'
          : 'border-gray-100',
        className,
      )}>
      {actions && actions.length > 0 && (
        <div className="absolute top-3 end-3">
          <CardActionMenu actions={actions} />
        </div>
      )}
      {children}
    </div>
  );
};

export interface CardIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

/**
 * Small icon-only button for card header rows (e.g. view, menu trigger).
 * Supports `forwardRef` so it works with Radix `asChild` (DropdownMenuTrigger, etc.).
 * Automatically stops click propagation so it doesn't trigger the card's onClick.
 */
export const CardIconButton = React.forwardRef<HTMLButtonElement, CardIconButtonProps>(
  ({ onClick, children, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        onClick={e => {
          e.stopPropagation();
          onClick?.(e);
        }}
        className={cn(
          'rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600',
          className,
        )}
        {...props}>
        {children}
      </button>
    );
  },
);
CardIconButton.displayName = 'CardIconButton';

export interface CardActionMenuProps {
  /** Same ActionButtonGroup[] used by ActionButton / DrawerFooter */
  actions: ActionButtonGroup[];
  /** Dropdown alignment (default: 'end') */
  align?: 'start' | 'end';
  className?: string;
}

/**
 * Three-dot action menu for cards.
 * Accepts the same `ActionButtonGroup[]` as the drawer footer ActionButton.
 * Hides itself when all actions are hidden.
 */
export const CardActionMenu: React.FC<CardActionMenuProps> = ({
  actions,
  align = 'end',
  className,
}) => {
  const visibleActions = useMemo(() => actions.filter(a => !a.hide), [actions]);

  if (visibleActions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <CardIconButton className={className}>
          <MoreVerticalIcon className="size-4" />
        </CardIconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[200px]">
        {visibleActions.map(action => (
          <DropdownMenuItem
            key={action.id}
            disabled={action.disabled || action.isLoading}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (!action.disabled && !action.isLoading && action.onClick) {
                action.onClick();
              }
            }}
            className={cn(
              'cursor-pointer px-3 py-2.5',
              action.highlight && 'font-bold text-blue-600 bg-blue-50/50',
              action.isLoading && 'opacity-75 cursor-wait',
            )}>
            <div className="flex items-start gap-3 w-full">
              {action.icon && (
                <div className="flex-shrink-0 mt-0.5 text-gray-500">{action.icon}</div>
              )}
              <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                <span
                  className={cn(
                    'font-medium text-sm',
                    action.highlight && 'text-blue-600',
                    action.disabled && 'text-gray-400',
                  )}>
                  {action.label}
                </span>
                {action.description && (
                  <span className="text-xs text-muted-foreground">{action.description}</span>
                )}
              </div>
              {action.isLoading && (
                <div className="flex-shrink-0 mt-0.5">
                  <Spinner />
                </div>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
